import { Expression } from 'mapbox-gl'

import api from 'src/api'
import { Provider, Offense, LightOffense } from 'src/api/gen/api'
import { MAP_LAYERS } from 'src/commons/map/types'
import { DEFAULT_MAIN_COLOR } from 'src/components/ColorDot'
import { POLICY_TYPES_ICONS } from 'src/components/Offenses'

import { IClickedOffensePoint } from './OffensePopup'
import { DISPLAY_LOGO_BREAKPOINT, SOURCE_NAME } from './utils'

export const MAP_VIEW_STORAGE = 'plato.supervision.satellite'

export function offensesToGeoJSON(
  offenses: LightOffense[],
  offenseToProperties: (o: any) => { [key: string]: any } = (o) => o
) {
  const features = offenses
    .filter((offense: LightOffense) => offense.gps_point && offense.gps_point.length)
    .map((offense: LightOffense) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: offense.gps_point,
      },
      properties: offenseToProperties(offense),
    }))

  return {
    type: 'FeatureCollection',
    features,
  }
}

export async function addOffensesToMap(
  map: mapboxgl.Map,
  assets: LightOffense[],
  activeProviders: Provider[],
  setClickedOffensePoint: (clickedOffensePoint: IClickedOffensePoint | undefined) => void,
  onLoaded?: () => void
) {
  map.addSource(SOURCE_NAME, {
    type: 'geojson',
    data: offensesToGeoJSON(assets),
  } as any)

  const circleColorMapping = activeProviders.map((provider) => [
    provider.id,
    (provider.colors && provider.colors.primary) || DEFAULT_MAIN_COLOR,
  ])

  map.addLayer({
    id: MAP_LAYERS.DeviceHighZoom,
    type: 'circle',
    maxzoom: DISPLAY_LOGO_BREAKPOINT,
    source: SOURCE_NAME,
    paint: {
      'circle-radius': {
        base: 2,
        stops: [
          [1, 1],
          [14, 6],
          [15, 7],
          [DISPLAY_LOGO_BREAKPOINT, 14],
        ],
      },
      'circle-color': [
        'match',
        ['get', 'provider_id'],
        ...Array.prototype.concat(...circleColorMapping),
        /* other */ '#ccc',
      ] as Expression,
    },
  })

  map.addLayer({
    id: MAP_LAYERS.DeviceLowZoom,
    source: SOURCE_NAME,
    type: 'symbol',
    minzoom: DISPLAY_LOGO_BREAKPOINT,
    layout: {
      'icon-image': [
        'match',
        ['get', 'policy_type'],
        ...Array.prototype.concat(...Object.keys(POLICY_TYPES_ICONS).map((key) => [key, key])),
        'unspecified',
      ] as Expression,
      'icon-size': 1,
      'icon-allow-overlap': true,
      'icon-ignore-placement': false,
    },
  })

  const displayOffensePopup = (layer: string) => async (event: any) => {
    const clickedPoint = map
      ? map.queryRenderedFeatures(event.point, {
          layers: [layer],
        })
      : []
    if (clickedPoint.length && clickedPoint[0].properties) {
      const offense = (await api.offenses.offenseList({ pk: clickedPoint[0].properties.pk }))['results'][0] as Offense
      const coordinates = event.lngLat
      setClickedOffensePoint(undefined)
      setClickedOffensePoint({ offense, coordinates })
    }
  }

  map.on('click', displayOffensePopup(MAP_LAYERS.DeviceHighZoom))
  map.on('click', displayOffensePopup(MAP_LAYERS.DeviceLowZoom))

  // When all loading operations are done, the map enters an idle state
  if (onLoaded) {
    map.on('idle', onLoaded)
  }
}
